import { create15Times, Reservation, ReservationStatus, Schedule } from '@raysee/schema';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button, ControlLabel, Form, FormGroup, HelpBlock, Modal, SelectPicker } from 'rsuite';

dayjs.extend(isBetween);

interface Props {
  isOpen: boolean;
  selectedSchedule: Schedule;
  currentSchedules: Schedule[];
  currentReservations: Reservation[];
  isManager: boolean;
  onClose: () => void;
  onEdit: (_: Pick<Schedule, 'id' | 'startTime' | 'endTime'>) => void;
  onDelete: (_: Pick<Schedule, 'id'>) => void;
}

interface FieldValues {
  startTime: number;
  endTime: number;
}

export const EditScheduleModal = (props: Props) => {
  const {
    control,
    getValues,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FieldValues>();

  const selectedStartTime = watch('startTime');
  const selectedEndTime = watch('endTime');
  const isChangeValid = selectedStartTime != null && !!selectedEndTime;
  const isPastSchedule = dayjs(props.selectedSchedule.endTime).isBefore(dayjs());

  useEffect(() => {
    if (selectedStartTime != null) {
      setValue('endTime', undefined);
    }
  }, [selectedStartTime, setValue]);

  // 開始は00:00~23:45
  const startStarted = dayjs().startOf('day');
  const startEnded = dayjs().startOf('day').add(1, 'day').subtract(15, 'minute');
  const startTimes = create15Times(startStarted, startEnded);

  // 終了は00:15~翌06:00
  const endStarted = dayjs().startOf('day').add(15, 'minute');
  const endEnded = dayjs().startOf('day').add(1, 'day').add(6, 'hour');
  const endTimes = create15Times(endStarted, endEnded);

  const filteredEndTimes =
    selectedStartTime != null
      ? endTimes.filter((time) => {
          if (time.value <= selectedStartTime) return false;

          const startMinutes = selectedStartTime % 60;
          const endMinutes = time.value % 60;
          return startMinutes === endMinutes;
        })
      : [];

  useEffect(() => {
    if (props.selectedSchedule) {
      const start = dayjs(props.selectedSchedule.startTime);
      const end = dayjs(props.selectedSchedule.endTime);
      setValue('startTime', start.diff(start.startOf('day'), 'minutes'));
      setValue('endTime', end.diff(start.startOf('day'), 'minutes'));
    }
  }, [props.selectedSchedule, setValue]);

  const onClickSubmit = () => {
    if (!props.selectedSchedule) return;
    const { startTime, endTime } = getValues();

    if (startTime >= endTime) {
      alert('終了時間は開始時間以降にしてください');
      return;
    }
    if (endTime - startTime < 60) {
      alert('1時間以上のシフトを登録してください');
      return;
    }
    const afterStartTime = dayjs(props.selectedSchedule.startTime)
      .startOf('day')
      .add(startTime, 'minutes');
    const afterEndTime = dayjs(props.selectedSchedule.startTime)
      .startOf('day')
      .add(endTime, 'minutes');
    if (afterStartTime.isBefore(dayjs())) {
      alert('過去のシフトは登録できません');
      return;
    }

    if (endTime - startTime > 24 * 60) {
      alert('最大24時間までとなります');
      return;
    }

    const beforeStartTime = dayjs(props.selectedSchedule.startTime);
    const beforeEndTime = dayjs(props.selectedSchedule.endTime);

    // 変更前と変更後の時間帯が重なっている部分に予約があるかチェック
    const activeReservations = props.currentReservations.filter((reservation) => {
      const reservationStart = dayjs(reservation.startTime);
      return (
        reservationStart.isBetween(beforeStartTime, beforeEndTime, null, '[]') &&
        !reservationStart.isBetween(afterStartTime, afterEndTime, null, '[]') &&
        reservation.status !== ReservationStatus.Canceled
      );
    });

    if (activeReservations.length > 0) {
      const reservationList = activeReservations
        .map(
          (r) => `・${dayjs(r.startTime).format('YYYY年MM月DD日(ddd) HH:mm')}開始 ${r.user.name}`,
        )
        .join('\n');
      alert(
        `変更前の時間帯に予約が残っています。キャンセル後に再度お試しください。\n\n${reservationList}`,
      );
      return;
    }

    props.onEdit({
      id: props.selectedSchedule.id,
      startTime: afterStartTime.toDate(),
      endTime: afterEndTime.toDate(),
    });
  };

  const onDelete = () => {
    if (props.selectedSchedule) {
      const activeReservations = props.currentReservations.filter((reservation) => {
        const reservationStart = dayjs(reservation.startTime);
        const beforeStartTime = dayjs(props.selectedSchedule.startTime);
        const beforeEndTime = dayjs(props.selectedSchedule.endTime);
        return (
          reservationStart.isBetween(beforeStartTime, beforeEndTime, null, '[]') &&
          reservation.status !== ReservationStatus.Canceled
        );
      });

      if (activeReservations.length > 0) {
        const reservationList = activeReservations
          .map(
            (r) => `・${dayjs(r.startTime).format('YYYY年MM月DD日(ddd) HH:mm')}開始 ${r.user.name}`,
          )
          .join('\n');
        alert(
          `変更前の時間帯に予約が残っています。キャンセル後に再度お試しください。\n\n${reservationList}`,
        );
        return;
      }

      props.onDelete({ id: props.selectedSchedule.id });
    }
  };

  return (
    <Modal show={props.isOpen} onHide={props.onClose} size="xs">
      <Modal.Header>
        <Modal.Title>
          スケジュール変更
          {props.selectedSchedule
            ? dayjs(props.selectedSchedule?.startTime).format('YYYY/MM/DD')
            : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form fluid layout="inline">
          <FormGroup>
            <ControlLabel>開始</ControlLabel>
            <Controller
              name="startTime"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <SelectPicker data={startTimes} placeholder="HH:MM" searchable={false} {...field} />
              )}
            />
            {errors.startTime && <HelpBlock>{errors.startTime}</HelpBlock>}
          </FormGroup>
          <FormGroup>
            <ControlLabel>終了</ControlLabel>
            <Controller
              name="endTime"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <SelectPicker
                  data={filteredEndTimes}
                  placeholder="HH:MM"
                  searchable={false}
                  disabled={selectedStartTime == null}
                  locale={{ noResultsText: '開始時間を選んでください' }}
                  {...field}
                />
              )}
            />
            {errors.endTime && <HelpBlock>{errors.endTime}</HelpBlock>}
          </FormGroup>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={onClickSubmit}
          appearance="primary"
          color="blue"
          disabled={!isChangeValid || isPastSchedule}
        >
          変更
        </Button>
        <Button onClick={onDelete} color="red" disabled={isPastSchedule}>
          削除
        </Button>
        <Button onClick={props.onClose} appearance="subtle">
          キャンセル
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
